import React, { useState, useRef } from "react";
import Hls from "hls.js";

const API_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined"
    ? window.location.origin
    : "http://localhost:5000");

function createAuthHlsLoader(authorizationToken) {
  return class AuthLoader extends Hls.DefaultConfig.loader {
    load(context, config, callbacks) {
      if (authorizationToken && context?.url) {
        try {
          const u = new URL(context.url);
          if (!u.searchParams.get("Authorization")) {
            u.searchParams.set("Authorization", authorizationToken);
            context.url = u.toString();
          }
        } catch (_) {
          /* keep original url */
        }
      }
      super.load(context, config, callbacks);
    }
  };
}

export default function TestStorage() {
  const [file, setFile] = useState(null);
  const [fileUuid, setFileUuid] = useState("");
  const [streamUrl, setStreamUrl] = useState("");
  const [streamType, setStreamType] = useState("");
  const [ffmpegStatus, setFfmpegStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const destroyHls = () => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  };

  // -----------------------
  // Upload → optional HLS
  // -----------------------
  const handleUpload = async () => {
    if (!file) return alert("Select a file");

    setBusy(true);
    setStatusMsg("Requesting upload target...");

    try {
      const res = await fetch(`${API_URL}/api/storage/upload-targets`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem("access_token")
            ? { Authorization: `Bearer ${localStorage.getItem("access_token")}` }
            : {}),
        },
        body: JSON.stringify({
          fileName: file.name,
          type: "video",
          size: file.size,
          isPrivate: 1, // private video → HLS pipeline
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "upload-targets failed");

      const { fileUuid: uuid, filePath, targets, hlsEnabled } = data;
      setFileUuid(uuid);

      const target = targets[0];
      setStatusMsg(`Uploading to B2 as ${filePath}...`);

      const uploadRes = await fetch(target.uploadUrl, {
        method: "POST",
        headers: {
          Authorization: target.authorizationToken,
          "X-Bz-File-Name": encodeURIComponent(filePath),
          "Content-Type": file.type || "b2/x-auto",
          "X-Bz-Content-Sha1": "do_not_verify",
        },
        body: file,
      });

      if (!uploadRes.ok) {
        throw new Error(`B2 upload failed (${uploadRes.status})`);
      }

      if (hlsEnabled) {
        // Fire-and-forget — process returns 202 and runs in background
        fetch(`${API_URL}/api/storage/${uuid}/process`, {
          method: "POST",
          headers: {
            ...(localStorage.getItem("access_token")
              ? { Authorization: `Bearer ${localStorage.getItem("access_token")}` }
              : {}),
          },
        }).catch((err) => console.warn("HLS queue failed", err));
        setFfmpegStatus("queued");
        setStatusMsg("Upload complete (HLS queued in background)");
      } else {
        setStatusMsg("Upload complete (raw, no HLS).");
      }
    } catch (err) {
      console.error(err);
      setStatusMsg(err.message || "Upload failed");
      alert(err.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  // -----------------------
  // Stream
  // -----------------------
  const handleStream = async () => {
    if (!fileUuid) return alert("Upload first");

    setBusy(true);
    setStatusMsg("Fetching stream URL...");

    try {
      const res = await fetch(`${API_URL}/api/storage/stream/${fileUuid}`, {
        headers: {
          ...(localStorage.getItem("access_token")
            ? { Authorization: `Bearer ${localStorage.getItem("access_token")}` }
            : {}),
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "stream failed");

      setStreamUrl(data.url);
      setStreamType(data.type);
      setFfmpegStatus(data.ffmpegStatus || "");

      const video = videoRef.current;
      destroyHls();

      if (data.type === "hls" && Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          loader: createAuthHlsLoader(data.authorizationToken),
        });
        hlsRef.current = hls;
        hls.loadSource(data.url);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_e, errorData) => {
          console.error("HLS error", errorData);
        });
        setStatusMsg("Playing HLS");
      } else if (data.type === "hls" && video.canPlayType("application/vnd.apple.mpegurl")) {
        // Safari native HLS — auth must already be on the master URL;
        // relative segments may still fail without cookie/auth rewrite.
        video.src = data.url;
        setStatusMsg("Playing HLS (native)");
      } else {
        video.src = data.url;
        setStatusMsg(
          data.ffmpegStatus && data.ffmpegStatus !== "completed"
            ? `Playing raw (ffmpeg: ${data.ffmpegStatus})`
            : "Playing raw"
        );
      }
    } catch (err) {
      console.error(err);
      setStatusMsg(err.message || "Stream failed");
      alert(err.message || "Stream failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: "120px" }}>
      <h2>Storage Test Page</h2>

      <div>
        <input
          type="file"
          accept="video/*"
          disabled={busy}
          onChange={(e) => setFile(e.target.files[0])}
        />
        <button onClick={handleUpload} disabled={busy || !file}>
          Upload + HLS
        </button>
      </div>

      <br />

      <div>
        <strong>File UUID:</strong> {fileUuid || "—"}
      </div>
      <div>
        <strong>ffmpeg:</strong> {ffmpegStatus || "—"}
      </div>
      <div>
        <strong>Status:</strong> {statusMsg || "—"}
      </div>

      <br />

      <button onClick={handleStream} disabled={busy || !fileUuid}>
        Play Video
      </button>

      <br />
      <br />

      <div>
        <strong>Stream Type:</strong> {streamType || "—"}
      </div>
      {streamUrl ? (
        <div style={{ fontSize: 12, wordBreak: "break-all", maxWidth: 640 }}>
          {streamUrl}
        </div>
      ) : null}

      <br />

      <video
        ref={videoRef}
        controls
        width="600"
        crossOrigin="anonymous"
        style={{ border: "1px solid #ccc" }}
      />
    </div>
  );
}
